import { useEffect, useRef } from 'react';
import { useAvPrefs } from '../audio/prefs';

/**
 * Look direction in [-1, 1] from the pointer anywhere on the page, or the gyroscope on phones.
 * Gated by the player's motion choice; iOS gyroscope access is requested by the start prompt or the
 * motion toggle (see audio/prefs.ts), never by a button on the stage.
 */
export function useTilt(enabled: boolean) {
  const look = useRef({ x: 0, y: 0, source: 'none' as 'none' | 'pointer' | 'gyro' });
  const prefs = useAvPrefs();
  const granted = prefs.gyroGranted;
  const pinnedLook = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('look');
  enabled = enabled && (prefs.motion || pinnedLook);

  useEffect(() => {
    if (!enabled) {
      look.current.x = 0;
      look.current.y = 0;
      look.current.source = 'none';
      return;
    }
    // QA: ?look=1,1 pins the view at full tilt (used by the Playwright seam checks)
    const pinned = new URLSearchParams(window.location.search).get('look');
    if (pinned) {
      const [x, y] = pinned.split(',').map(Number);
      look.current.x = Math.max(-1, Math.min(1, x || 0));
      look.current.y = Math.max(-1, Math.min(1, y || 0));
      look.current.source = 'pointer';
      return;
    }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch' && look.current.source === 'gyro') return;
      look.current.x = (e.clientX / window.innerWidth) * 2 - 1;
      look.current.y = -((e.clientY / window.innerHeight) * 2 - 1);
      if (look.current.source !== 'gyro') look.current.source = 'pointer';
    };
    let base: { b: number; g: number } | null = null;
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.beta == null || e.gamma == null) return;
      if (!base) base = { b: e.beta, g: e.gamma };
      look.current.source = 'gyro';
      look.current.x = Math.max(-1, Math.min(1, (e.gamma - base.g) / 22));
      look.current.y = Math.max(-1, Math.min(1, -(e.beta - base.b) / 22));
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    if (granted) window.addEventListener('deviceorientation', onOrient);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('deviceorientation', onOrient);
    };
  }, [enabled, granted]);

  return { look };
}
